import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './Modal.css';

/**
 * Modal Component - Reusable modal dialog
 * Supports confirm, input, and custom content modals
 */
const Modal = ({
  isOpen,
  onClose,
  title = '',
  message,
  type = 'confirm', // 'confirm', 'input', 'custom'
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = true,
  icon,
  children,
  inputValue,
  onInputChange,
  inputPlaceholder,
  inputLabel,
  isDangerous = false,
  isLoading = false,
  className = ''
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(inputValue);
    } else if (onClose && !showCancel) {
      // If no onConfirm and no cancel button, treat confirm as close
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type === 'input' && inputValue) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal-content ${className}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          {icon && <span className="modal-icon">{icon}</span>}
          <h3 className="modal-title">{title}</h3>
          {onClose && (
            <button
              className="modal-close-btn"
              onClick={handleCancel}
              type="button"
            >
              {'×'}
            </button>
          )}
        </div>

        {/* Body */}
        <div className="modal-body">
          {type === 'input' ? (
            <>
              {inputLabel && <label className="modal-input-label">{inputLabel}</label>}
              <input
                autoFocus
                className="modal-input"
                onChange={(e) => onInputChange && onInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                type="text"
                value={inputValue || ''}
              />
              {message && <p className="modal-message">{message}</p>}
            </>
          ) : type === 'custom' ? (
            children
          ) : (
            <p className="modal-message">{message}</p>
          )}
        </div>

        {/* Footer */}
        {type !== 'custom' && (
          <div className="modal-footer">
            {showCancel && (
              <button
                className="modal-btn modal-btn-cancel"
                disabled={isLoading}
                onClick={handleCancel}
                type="button"
              >
                {cancelText}
              </button>
            )}
            <button
              className={`modal-btn modal-btn-confirm ${isDangerous ? 'dangerous' : ''}`}
              disabled={isLoading || (type === 'input' && !inputValue)}
              onClick={handleConfirm}
              type="button"
            >
              {isLoading ? '⏳' : confirmText}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  cancelText: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  confirmText: PropTypes.string,
  icon: PropTypes.string,
  inputLabel: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  inputValue: PropTypes.string,
  isDangerous: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onInputChange: PropTypes.func,
  showCancel: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.oneOf(['confirm', 'input', 'custom'])
};

Modal.defaultProps = {
  cancelText: 'Cancel',
  children: null,
  className: '',
  confirmText: 'OK',
  icon: null,
  inputLabel: null,
  inputPlaceholder: '',
  inputValue: '',
  isDangerous: false,
  isLoading: false,
  message: '',
  onCancel: null,
  onClose: null,
  onConfirm: null,
  onInputChange: null,
  showCancel: true,
  type: 'confirm'
};

export default Modal;
